import {
  GetTransactionByIdRequestPayload,
  GetTransactionByIdSuccessPayload,
} from '@/pages/Pricers/store/pricer/actions';
import { GenericReportDownload } from '@/types/downloadReports';

import { createAction, createAsyncAction } from 'typesafe-actions';

import {
  BCVA,
  CDS,
  ClosestValueTradeDate,
  TermSheetOutputTradeResponse,
  TransactionCashFlows,
  TransactionInfoAndTitle,
  TransactionNotionalSchedule,
  TransactionValueDetail,
} from '../types';

export type TransactionDetailsRequestPayload = {
  transaction_id: number;
};

export type GetTransactionValueDetailSuccessPayload = {
  data: TransactionValueDetail;
};

export const getTransactionDetails = createAsyncAction(
  'transactionDetail/getTransactionDetails/request',
  'transactionDetail/getTransactionDetails/success',
  'transactionDetail/getTransactionDetails/failure',
)<
  TransactionDetailsRequestPayload,
  GetTransactionValueDetailSuccessPayload,
  undefined
>();

export type GetTransactionInfoAndTitleSuccessPayload = {
  data: TransactionInfoAndTitle;
};

export const getTransactionInfoAndTitle = createAsyncAction(
  'transactionDetail/getTransactionInfoAndTitle/request',
  'transactionDetail/getTransactionInfoAndTitle/success',
  'transactionDetail/getTransactionInfoAndTitle/failure',
)<
  TransactionDetailsRequestPayload,
  GetTransactionInfoAndTitleSuccessPayload,
  undefined
>();

export type GetTransactionNotionalScheduleSuccessPayload = {
  data: TransactionNotionalSchedule;
};

export const getTransactionNotionalSchedule = createAsyncAction(
  'transactionDetail/getTransactionNotionalSchedule/request',
  'transactionDetail/getTransactionNotionalSchedule/success',
  'transactionDetail/getTransactionNotionalSchedule/failure',
)<
  TransactionDetailsRequestPayload,
  GetTransactionNotionalScheduleSuccessPayload,
  undefined
>();

export type GetTransactionCashFlowsRequestPayload = {
  transaction_id: number;
  cashflow_date: string;
};
export type GetTransactionCashflowsSuccessPayload = {
  data: TransactionCashFlows;
};
export const getTransactionCashflows = createAsyncAction(
  'transactionDetail/getTransactionCashflows/request',
  'transactionDetail/getTransactionCashflows/success',
  'transactionDetail/getTransactionCashflows/failure',
)<
  GetTransactionCashFlowsRequestPayload,
  GetTransactionCashflowsSuccessPayload,
  undefined
>();

export type GetHistoricalTradeReportRequestPayload = {
  transaction_id: number;
};
export type GetHistoricalTradeReportSuccessPayload = {
  data: GenericReportDownload;
};

export const getHistoricalTradeReportDownload = createAsyncAction(
  'transactionDetail/getHistoricalTradeReportDownload/request',
  'transactionDetail/getHistoricalTradeReportDownload/success',
  'transactionDetail/getHistoricalTradeReportDownload/failure',
)<
  GetHistoricalTradeReportRequestPayload,
  GetHistoricalTradeReportSuccessPayload,
  undefined
>();

//bcva data
export type GetBCVASuccessPayload = {
  data: BCVA;
};

export const getBCVA = createAsyncAction(
  'transactionDetail/getBCVA/request',
  'transactionDetail/getBCVA/success',
  'transactionDetail/getBCVA/failure',
)<
  GetTransactionCashFlowsRequestPayload, // same payload as getTransactionCashflows
  GetBCVASuccessPayload,
  undefined
>();

//cds data
export type GetCDSSuccessPayload = {
  data: CDS[];
};
export const getCDS = createAsyncAction(
  'transactionDetail/getCDS/request',
  'transactionDetail/getCDS/success',
  'transactionDetail/getCDS/failure',
)<
  GetTransactionCashFlowsRequestPayload, // same payload as getTransactionCashflows
  GetCDSSuccessPayload,
  undefined
>();

export type GetApproveTradesRequestPayload = {
  transaction_ids: number[];
  message?: string;
};
export const approveTrade = createAsyncAction(
  'transactionDetail/approveTrade/request',
  'transactionDetail/approveTrade/success',
  'transactionDetail/approveTrade/failure',
)<GetApproveTradesRequestPayload, undefined, undefined>();

export const rejectTrade = createAsyncAction(
  'transactionDetail/rejectTrade/request',
  'transactionDetail/rejectTrade/success',
  'transactionDetail/rejectTrade/failure',
)<GetApproveTradesRequestPayload, undefined, undefined>();

export const amendTrade = createAsyncAction(
  'transactionDetail/amendTrade/request',
  'transactionDetail/amendTrade/success',
  'transactionDetail/amendTrade/failure',
)<GetApproveTradesRequestPayload, undefined, undefined>();

//cva & vva report download
export type GetCVAReportRequestPayload = {
  transaction_id: number;
  curve_date: string;
};
export type GetXVAReportRequestPayload = {
  transaction_id: number;
  xva_date: string;
  cds_curve: [] | null;
};

export type GetCVAXVAReportSuccessPayload = {
  data: GenericReportDownload;
};

export const getCVAReportDownload = createAsyncAction(
  'transactionDetail/getCVAXVAReportDownload/request',
  'transactionDetail/getCVAXVAReportDownload/success',
  'transactionDetail/getCVAXVAReportDownload/failure',
)<GetCVAReportRequestPayload, GetCVAXVAReportSuccessPayload, undefined>();

export const getXVAReportDownload = createAsyncAction(
  'transactionDetail/getXVAReportDownload/request',
  'transactionDetail/getXVAReportDownload/success',
  'transactionDetail/getXVAReportDownload/failure',
)<GetXVAReportRequestPayload, GetCVAXVAReportSuccessPayload, undefined>();

export const reset = createAction('transactionDetail/reset')();

//closest value date
export type GetClosestValueDateSuccessPayload = {
  data: ClosestValueTradeDate;
};

export const getClosestValueDate = createAsyncAction(
  'transactionDetail/getClosestValueDate/request',
  'transactionDetail/getClosestValueDate/success',
  'transactionDetail/getClosestValueDate/failure',
)<
  TransactionDetailsRequestPayload,
  GetClosestValueDateSuccessPayload,
  undefined
>();

export const getTradeById = createAsyncAction(
  'transactionDetail/getTradeById/request',
  'transactionDetail/getTradeById/success',
  'transactionDetail/getTradeById/failure',
)<
  GetTransactionByIdRequestPayload,
  GetTransactionByIdSuccessPayload,
  undefined
>();

export const selectCurrency = createAction(
  'transactionDetail/selectCurrency',
)<string>();

export type GetTermSheetOutputTradesRequestPayload = {
  transaction_id_list: number[];
  pre_trade: boolean;
  include_rfp: boolean;
};

export type GetTermSheetOutputTradesSuccessPayload = {
  data: TermSheetOutputTradeResponse[];
};

export const getTermsheetOutputTrades = createAsyncAction(
  'transactionDetail/getTermsheetOutputTrades/request',
  'transactionDetail/getTermsheetOutputTrades/success',
  'transactionDetail/getTermsheetOutputTrades/failure',
)<
  GetTermSheetOutputTradesRequestPayload,
  GetTermSheetOutputTradesSuccessPayload,
  undefined
>();
